import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { PrimaryButton, GhostButton } from "@/components/ui-bits";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sid) { setState("timeout"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
      } catch {}
      tries.current += 1;
      if (tries.current > 8) { setState("timeout"); return; }
      if (!stop) setTimeout(poll, 1800);
    };
    poll();
    return () => { stop = true; };
  }, [sid]);

  const goto = () => {
    if (info?.kind === "event") nav("/events");
    else if (info?.kind === "membership") nav(`/communities/${info.ref_id}`);
    else nav("/marketplace");
  };

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      {state === "checking" && (<><Loader2 className="h-10 w-10 animate-spin text-[#FF6B00] mx-auto" /><h1 className="text-2xl font-bold mt-5">Confirming your payment…</h1><p className="text-[#071126]/60 mt-2">Hang tight, this only takes a moment.</p></>)}
      {state === "paid" && (
        <div className="pa-pop">
          <span className="h-16 w-16 rounded-2xl bg-[#8FAF9B]/20 grid place-items-center mx-auto"><CheckCircle2 className="h-9 w-9 text-[#4d7361]" /></span>
          <h1 className="text-3xl font-extrabold mt-5">Payment successful!</h1>
          <p className="text-[#071126]/65 mt-2">{info?.title ? `You're all set with "${info.title}".` : "Thank you — your purchase is confirmed."}</p>
          <PrimaryButton onClick={goto} className="mt-7" data-testid="payment-continue">Continue<ArrowRight className="h-4 w-4" /></PrimaryButton>
        </div>
      )}
      {state === "timeout" && (<><h1 className="text-2xl font-bold">We couldn't confirm yet</h1><p className="text-[#071126]/60 mt-2">If you completed payment it may take a minute. Check your notifications.</p><GhostButton as={Link} to="/home" className="mt-6">Back to home</GhostButton></>)}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      <span className="h-16 w-16 rounded-2xl bg-[#071126]/6 grid place-items-center mx-auto"><XCircle className="h-9 w-9 text-[#55708F]" /></span>
      <h1 className="text-3xl font-extrabold mt-5">Checkout cancelled</h1>
      <p className="text-[#071126]/65 mt-2">No worries — nothing was charged. You can try again anytime.</p>
      <GhostButton as={Link} to="/marketplace" className="mt-7">Back to marketplace</GhostButton>
    </div>
  );
}
